import camelCase from 'lodash-es/camelCase';
import get from 'lodash-es/get';
import isFinite from 'lodash-es/isFinite';
import isPlainObject from 'lodash-es/isPlainObject';

import { INBOUND_LEADS, LICENSE_COLORS, ORDER, SELLER_STATUSES } from '../constants';

const BULK = 'BULK';
const MOUNT_POINT_CLASS = '.mount-point-container';

/**
 * Filters the list of `items` based on options selected in the `filters` object
 * @param {array} items A list of items to filter
 * @param {IFilter} options an options object for the filtering configuration
 */
export function filterItems(items, filters) {
  if (!Object.keys(filters).length) {
    return items;
  }

  return items.filter((item) => {
    for (const key in filters) {
      // eg. `brand`, `category`, `status`...
      const filter = filters[key]; // IFilter;
      const filterValues = filter.filterBy; // eg. ['1906', 'LuvBuds'], ['vape', 'pen', 'flower']
      let itemValue = get(item, key.split('.')); // eg `LuvBuds`, 'vape'

      // TODO take care of this upstream. ie write custom filter function if itemValue is an object
      // ie.  if (typeof itemValue !== 'string' || typeof itemValue !== 'number' )
      if (isPlainObject(itemValue)) {
        itemValue = item[key].id;
      }

      if ('customFilter' in filter) {
        // custom function should return `false` when the filter logic does not match
        if (!filter.customFilter(filterValues, itemValue, item)) {
          return false;
        }
      } else {
        if (!Array.isArray(itemValue) && !~filterValues.indexOf(itemValue)) {
          return false;
        }
      }
    }

    return true;
  });
}

/**
 * Sorts an array of items
 * @param {Array} items the list of things to sort
 * @param {ISort} options an options object for the sorting configuration
 *   sortBy: the object key to sort on
 *   sortDesc: whether to sort descending or not
 *   customSorter: a custom sorting function
 *
 * https://github.com/vuetifyjs/vuetify/blob/master/packages/vuetify/src/util/helpers.ts#L381
 */
export function sortItems(items, options) {
  const { sortBy, sortDesc, customSorter, locale = 'en' } = options;
  const isObject = items.length && typeof items[0] === 'object';

  if (!sortBy && isObject) {
    return items;
  }

  return [...items].sort((a, b) => {
    let sortA = get(a, sortBy) || a;
    let sortB = get(b, sortBy) || b;

    if (sortDesc) {
      [sortA, sortB] = [sortB, sortA];
    }

    if (customSorter) {
      return customSorter(sortA, sortB);
    }

    [sortA, sortB] = [sortA, sortB].map((s) => (s || '').toString().toLocaleLowerCase());

    if (sortA !== sortB) {
      const sortResult =
        !isNaN(sortA) && !isNaN(sortB) ? Number(sortA) - Number(sortB) : sortA.localeCompare(sortB, locale);

      if (sortResult) {
        return sortResult;
      }
    }

    return 0;
  });
}

/**
 * Removes all accents/diacritics and converts to lower case
 * @param {string} text to normalize
 */
export function normalizeText(text) {
  if (isFinite(text)) {
    text = text.toString();
  }

  if (!text || typeof text !== 'string') {
    return '';
  }

  /**
   * NFD splits the canonical form and the regex removed only accent characters
   * see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/normalize for more detail.
   */
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/**
 * Generates a hash code from a string
 * @param {string} s The string to generate a hash from
 * @see https://stackoverflow.com/questions/7616461/generate-a-hash-from-string-in-javascript/52171480
 */
export function hashCode(s) {
  return s.split('').reduce((a, b) => {
    a = (a << 5) - a + b.charCodeAt(0);
    return a & a;
  }, 0);
}

/**
 * Returns the mount point for elements that will reattach themselves, or creates one if not available
 */
export function getMountPoint() {
  const el = document.querySelector(MOUNT_POINT_CLASS);

  if (!el) {
    const div = document.createElement('div');

    div.className = MOUNT_POINT_CLASS.slice(1, MOUNT_POINT_CLASS.length);
    return document.body.appendChild(div);
  }

  return el;
}

/**
 * Retrieves the value of a css variable
 *
 * @param {string} name name of the css var, with or without `--`
 * @return {string} value of the css var
 */
export function getCssVar(name) {
  let variable = name;

  if (variable.substring(0, 2) !== '--') {
    variable = `--${variable}`;
  }

  return getComputedStyle(document.documentElement).getPropertyValue(variable);
}

/**
 * Format the license type text to display
 * @param {string} licenseType the first three characters of the licenseType
 */
export function formatLicenseTypeText(licenseType = '') {
  const type = licenseType.toUpperCase();

  return type === BULK ? type : type.slice(0, 3);
}

export function generateColorClass(type, value) {
  let color;

  try {
    switch (type) {
      case 'license':
        value = formatLicenseTypeText(value);
        color = LICENSE_COLORS[value];
        break;
      case 'order':
        color = 'bg-' + ORDER[value.toUpperCase()].color;
        break;
      case 'certified-seller':
        color = SELLER_STATUSES[camelCase(value)].color;
        break;
      case 'inbound-leads':
        color = INBOUND_LEADS[value.toUpperCase()].color;
        break;
    }

    return color || 'bg-ice-500';
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (err) {
    return 'bg-ice-500';
  }
}
